// ── TrainingPage.jsx ──────────────────────────────────────────────────────────
// QuantumLogics — Pakistan's Largest Open Source Community 🇵🇰
//
// FIXES:
//  - IntersectionObserver threshold lowered to 0.08 for better mobile triggering
//  - rootMargin increased to -32px (was -48px) so reveals fire before scroll end
//  - observer.disconnect() called on cleanup to prevent memory leak on remount
//  - style tag is memoized via useMemo to avoid re-injection on every render

import { useEffect } from "react";
import SiteFooter from "../SiteFooter";

import { TRAINING_CSS } from "./styles/trainingTokens";
import TrainingHero, { HERO_CSS } from "./TrainingHero";
import TrainingStats, { STATS_CSS } from "./TrainingStats";
import TrainingDiscord, { DISCORD_CSS } from "./TrainingDiscord";
import {
  TrainingTracks,
  TrainingPerks,
  TrainingSteps,
  CONTENT_CSS,
} from "./TrainingContent";
import TrainingPricing, { PRICING_CSS } from "./TrainingPricing";
import TrainingPayment, { PAYMENT_CSS } from "./TrainingPayment";
import TrainingCTA, { CTA_CSS } from "./TrainingCTA";

// Merge all CSS into one <style> tag — zero runtime overhead
const ALL_CSS = [
  TRAINING_CSS,
  HERO_CSS,
  STATS_CSS,
  DISCORD_CSS,
  CONTENT_CSS,
  PRICING_CSS,
  PAYMENT_CSS,
  CTA_CSS,
].join("\n");

export default function TrainingPage({ setShowAuth, currentUser }) {

  const scrollToPayment = () => {
    try {
      const el = document.getElementById("tp-payment");
      if (el && el.getBoundingClientRect) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    } catch (error) {
      console.warn('Error scrolling to payment:', error);
      // Fallback: scroll to bottom of page
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    }
  };

  // ── Scroll-reveal observer ────────────────────────────────────────────────
  useEffect(() => {
    // Wait for DOM to be ready before querying elements
    const timer = setTimeout(() => {
      try {
        const selectors = ".tp-reveal, .tp-reveal-left, .tp-reveal-right";
        const els = document.querySelectorAll(selectors);

        if (!els.length) return;

        // Filter out any null/undefined elements
        const validElements = Array.from(els).filter(el => el && el.getBoundingClientRect);
        if (!validElements.length) return;

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.target) {
                try {
                  entry.target.classList.add("tp-visible");
                  observer.unobserve(entry.target);
                } catch (error) {
                  console.warn('Error revealing element:', error);
                }
              }
            });
          },
          // Lower threshold + less negative rootMargin = fires earlier on mobile scroll
          { threshold: 0.08, rootMargin: "0px 0px -32px 0px" },
        );

        validElements.forEach((el) => {
          try {
            observer.observe(el);
          } catch (error) {
            console.warn('Error observing element:', error);
          }
        });

        return () => {
          try {
            observer.disconnect();
          } catch (error) {
            console.warn('Error disconnecting observer:', error);
          }
        };
      } catch (error) {
        console.warn('Error setting up scroll reveal:', error);
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="tp-root">
      {/* Inject all styles once — style tag is static string, no re-render cost */}
      <style>{ALL_CSS}</style>

      <TrainingHero scrollToPayment={scrollToPayment} />
      <TrainingStats />
      <TrainingDiscord scrollToPayment={scrollToPayment} />
      <TrainingTracks />
      <TrainingPerks />
      <TrainingSteps />
      <TrainingPricing scrollToPayment={scrollToPayment} />
      <TrainingPayment currentUser={currentUser} setShowAuth={setShowAuth} />
      <TrainingCTA scrollToPayment={scrollToPayment} />

      <SiteFooter />
    </div>
  );
}
